import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Typography,
  FormControlLabel, // Nuevo
  Checkbox, // Nuevo
  Box // Nuevo para layout
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/es';
import api from '../api/api';

// Correo por defecto que se enviará si el checkbox está marcado
const DEFAULT_EMAIL = 'notienecorreo@acme.com.sv';

// Función para validar el formato del correo
const validateEmail = (email) => {
  if (!email) return true; // El campo no es obligatorio si lo usamos con el Checkbox (aunque la API lo requiera)
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const ClientePacienteModal = ({ open, handleClose, razas, onSaveSuccess }) => {
  const [pacienteFormData, setPacienteFormData] = useState({
    id_raza: '',
    nombre_cliente: '',
    email_cliente: '',
    nombre_paciente: '',
    fecha_nac_paciente: null,
  });
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [especies, setEspecies] = useState([]);
  const [emailError, setEmailError] = useState('');
  
  // Nuevo estado para el Checkbox
  const [noEmail, setNoEmail] = useState(false); 

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserIdPet(payload.id_pet || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        setError('Error al decodificar el token de usuario');
      }
    }
  }, []);

  useEffect(() => {
    const fetchEspecies = async () => {
      try {
        const response = await api.get('/especies/');
        setEspecies(response.data);
      } catch (error) {
        console.error('Error al cargar especies:', error);
      }
    };

    if (open) {
      fetchEspecies();
      setPacienteFormData({
        id_raza: '',
        nombre_cliente: '',
        email_cliente: '',
        nombre_paciente: '',
        fecha_nac_paciente: null,
      });
      setError(null);
      setEmailError('');
      setNoEmail(false); // Resetear checkbox
    }
  }, [open]);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setPacienteFormData({ ...pacienteFormData, email_cliente: email });
    
    if (email && !validateEmail(email)) {
      setEmailError('Formato de correo electrónico inválido');
    } else {
      setEmailError('');
    }
  };

  const handleNoEmailChange = (e) => {
    const checked = e.target.checked;
    setNoEmail(checked);
    
    if (checked) {
      // Si marca el checkbox, limpia el campo de texto y el error
      setPacienteFormData(prevData => ({ ...prevData, email_cliente: '' }));
      setEmailError('');
    }
  };

  const handleDateChange = (date) => {
    setPacienteFormData({ ...pacienteFormData, fecha_nac_paciente: date });
  };

  const handleSavePaciente = async () => {
    // 1. Validaciones
    if (!pacienteFormData.nombre_cliente.trim() || !pacienteFormData.nombre_paciente.trim()) {
      setError('Los campos Nombre Cliente y Nombre Paciente son obligatorios.');
      return;
    }
    
    // Si NO marcó el checkbox Y hay un error de email O el campo está vacío, detenemos.
    if (!noEmail && (emailError || !pacienteFormData.email_cliente.trim())) {
      setError(emailError || 'El email es obligatorio o debe marcar la casilla "No tiene correo".');
      return;
    }

    try {
      setError(null);

      // 2. Preparar el payload
      const finalEmail = noEmail 
        ? DEFAULT_EMAIL // Usa el correo por defecto si el checkbox está marcado
        : pacienteFormData.email_cliente; // Usa el correo ingresado

      const payload = {
        ...pacienteFormData,
        
        // Sobreescribir el email_cliente con el valor final
        email_cliente: finalEmail, 
        
        // Formatear la fecha
        fecha_nac_paciente: pacienteFormData.fecha_nac_paciente 
          ? pacienteFormData.fecha_nac_paciente.format('YYYY-MM-DD') 
          : null,
      };

      // 3. Construir FormData y enviar
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        // Enviar solo si el valor no es null (ej: fecha_nac_paciente opcional)
        if (value !== null) { 
            formData.append(key, value);
        }
      });

      await api.post('/clientes/cliente-y-paciente', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error('Error detallado al guardar cliente y paciente:', error);
      const errorMessage = error.response?.data?.detail 
        ? JSON.stringify(error.response.data.detail) 
        : error.message;

      setError('Error al guardar cliente y paciente: ' + errorMessage);
    }
  };

  const filteredRazas = userIdPet
    ? razas.filter((raza) => raza.id_pet === userIdPet && raza.habilitada)
    : [];

  // Deshabilitar botón de guardar si:
  const isSaveDisabled = 
    !!emailError || 
    !pacienteFormData.nombre_cliente.trim() || 
    !pacienteFormData.nombre_paciente.trim() ||
    (!noEmail && !pacienteFormData.email_cliente.trim()); // Si NO hay checkbox marcado Y el email está vacío

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" disableRestoreFocus={true} 
        disableScrollLock={true}>
        <DialogTitle>Nuevo Cliente y Paciente</DialogTitle>
        <DialogContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          
          <TextField
            label="Nombre Cliente *"
            fullWidth
            value={pacienteFormData.nombre_cliente}
            onChange={(e) =>
              setPacienteFormData({ ...pacienteFormData, nombre_cliente: e.target.value })
            }
            sx={{ mt: 2 }}
          />

          <TextField
            label="Nombre Paciente *"
            fullWidth
            value={pacienteFormData.nombre_paciente}
            onChange={(e) =>
              setPacienteFormData({ ...pacienteFormData, nombre_paciente: e.target.value })
            }
            sx={{ mt: 2 }}
          />

          {/* Bloque de Email con Checkbox */}
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Email Cliente *"
              fullWidth
              value={pacienteFormData.email_cliente}
              onChange={handleEmailChange}
              disabled={noEmail} // Deshabilitado si el checkbox está marcado
              error={!!emailError}
              helperText={emailError || (noEmail ? `Se usará: ${DEFAULT_EMAIL}` : '')}
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={noEmail} 
                  onChange={handleNoEmailChange} 
                />
              }
              label="El cliente no tiene correo electrónico"
              sx={{ ml: 0, mt: 1 }}
            />
          </Box>
          {/* Fin Bloque de Email */}

          <Autocomplete
            fullWidth
            options={filteredRazas}
            // LÓGICA DE CONCATENACIÓN: Muestra Especie - Raza
            getOptionLabel={(option) => {
              const especie = especies.find(e => e.id_especie === option.id_especie);
              const nombreEspecie = especie ? especie.nombre_especie : 'Especie Desconocida';
              return `${nombreEspecie} - ${option.nombre_raza}`;
            }}
            getOptionKey={(option) => option.id_raza}
            value={filteredRazas.find(raza => raza.id_raza === pacienteFormData.id_raza) || null}
            onChange={(event, newValue) => {
              setPacienteFormData(prevData => ({
                  ...prevData,
                  id_raza: newValue?.id_raza || '',
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Raza y Especie Paciente"
                sx={{ mt: 2 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
            disabled={!userIdPet}
          />

         <DatePicker
  label="Fecha Nacimiento Paciente"
  value={pacienteFormData.fecha_nac_paciente}
  onChange={handleDateChange}
  format="DD/MM/YYYY"
  disableAutoFocus
  openTo="year"
  views={['year', 'month', 'day']}
  slotProps={{
    textField: {
      fullWidth: true,
      sx: { mt: 2 },
      InputLabelProps: { shrink: true },
    },
    popper: {
      placement: 'bottom-start',
      strategy: 'fixed', // Clave definitiva para que no salte al (0,0) al interactuar con el año
    },
  }}
/>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button 
            onClick={handleSavePaciente} 
            variant="contained"
            disabled={isSaveDisabled}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ClientePacienteModal;